/**
 * 프로젝트 목록 페이지 (오프라인 캐시 지원)
 * - 검색 및 상태 필터 기능
 * - Pull-to-refresh (rAF 최적화)
 * - FAB(Floating Action Button)으로 새 프로젝트 생성
 * - 오프라인 모드 시 캐시 상태 시각화
 */

import { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, CloudOff, Lock } from "lucide-react";
import { useCachedProjects } from "@/hooks/useCachedProjects";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { useSyncQueue } from "@/hooks/useSyncQueue";
import { useToast } from "@/contexts/ToastContext";
import { OfflineBanner } from "@/components/ui/OfflineBanner";
import { SyncStatusBar } from "@/components/ui/SyncStatusBar";
import { SearchFilter } from "@/components/project/SearchFilter";
import { formatDateShort } from "@/lib/utils/date";
import { FullScreenSpinner } from "@/components/ui/FullScreenSpinner";
import {
  PROJECT_STATUS_LABELS,
  getProjectStatusStyle,
  type ProjectStatus,
} from "@ict/shared/constants/project.client";

interface Project {
  _id: string;
  number?: string;
  name: string;
  companyId: string;
  status?: ProjectStatus;
  startDate?: string;
  endDate?: string;
}

export function ProjectListPage() {
  const navigate = useNavigate();
  const { projects, loading, error, refetch, cachedProjectIds } = useCachedProjects();
  const { isOffline } = useNetworkStatus();
  const { pendingCount, isSyncing, progress, total, sync } = useSyncQueue();
  const { showToast } = useToast();

  // 검색 및 필터 상태
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<ProjectStatus | null>(null);

  // Pull-to-refresh 상태
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const isPulling = useRef(false);
  const currentDistance = useRef(0);
  const rafId = useRef<number | null>(null);

  const PULL_THRESHOLD = 80;

  // 필터링된 프로젝트 목록
  const filteredProjects = useMemo(() => {
    return projects.filter((project: Project) => {
      const matchesSearch =
        !searchQuery || project.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = !statusFilter || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchQuery, statusFilter]);

  const handleSearchChange = useCallback((search: string) => {
    setSearchQuery(search);
  }, []);

  const handleStatusChange = useCallback((status: ProjectStatus | null) => {
    setStatusFilter(status);
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      isPulling.current = true;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling.current) return;

    const touchY = e.touches[0].clientY;
    const distance = touchY - startY.current;

    if (distance > 0) {
      currentDistance.current = Math.min(distance, PULL_THRESHOLD * 1.5);

      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      rafId.current = requestAnimationFrame(() => {
        setPullDistance(currentDistance.current);
      });
    }
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }

    if (currentDistance.current >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true);
      await refetch();
      setIsRefreshing(false);
    }
    setPullDistance(0);
    currentDistance.current = 0;
    isPulling.current = false;
  }, [isRefreshing, refetch]);

  const handleResetFilters = useCallback(() => {
    setSearchQuery("");
    setStatusFilter(null);
  }, []);

  /**
   * 프로젝트 카드 클릭 핸들러
   * 오프라인 모드에서 캐시되지 않은 프로젝트 클릭 시 토스트 표시 및 진입 차단
   */
  const handleProjectClick = useCallback(
    (projectId: string) => {
      const isCached = cachedProjectIds.has(projectId);

      if (isOffline && !isCached) {
        showToast("이 프로젝트는 오프라인에서 사용할 수 없습니다", "warning");
        return;
      }

      navigate(`/projects/${projectId}`);
    },
    [isOffline, cachedProjectIds, navigate, showToast]
  );

  // 컴포넌트 언마운트 시 rAF 정리
  useEffect(() => {
    return () => {
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, []);

  if (loading) {
    return <FullScreenSpinner />;
  }

  if (error && projects.length === 0) {
    return (
      <div className="p-4">
        <p className="text-red-500">오류: {error.message}</p>
      </div>
    );
  }

  const getEmptyMessage = () => {
    if (projects.length === 0) {
      return "프로젝트가 없습니다.";
    }
    if (searchQuery && statusFilter) {
      return `"${searchQuery}" 검색 결과가 없습니다.`;
    }
    if (searchQuery) {
      return `"${searchQuery}" 검색 결과가 없습니다.`;
    }
    if (statusFilter) {
      const statusLabel = PROJECT_STATUS_LABELS[statusFilter];
      return `${statusLabel} 상태의 프로젝트가 없습니다.`;
    }
    return "프로젝트가 없습니다.";
  };

  const formatDate = (date?: string) => {
    if (!date) return "미정";
    const formatted = formatDateShort(date);
    return formatted || "미정";
  };

  return (
    <div className="relative h-full">
      {/* 스크롤 가능한 콘텐츠 영역 */}
      <div
        ref={containerRef}
        className="h-full overflow-auto bg-slate-50 p-4 pb-16"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull-to-refresh 인디케이터 */}
        {(pullDistance > 0 || isRefreshing) && (
          <div
            className="flex items-center justify-center transition-all"
            style={{ height: isRefreshing ? 50 : pullDistance }}
          >
            {isRefreshing ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                새로고침 중...
              </div>
            ) : (
              <div className="text-sm text-gray-400">
                {pullDistance >= PULL_THRESHOLD ? "놓으면 새로고침" : "↓ 당겨서 새로고침"}
              </div>
            )}
          </div>
        )}

        {/* 오프라인 배너 */}
        <OfflineBanner />

        {/* 동기화 상태 바 */}
        <div className="mb-4">
          <SyncStatusBar
            pendingCount={pendingCount}
            isSyncing={isSyncing}
            progress={progress}
            total={total}
            onSyncClick={sync}
            disabled={isOffline}
          />
        </div>

        {/* 검색 및 필터 + 새 프로젝트 버튼 */}
        <div className="sticky top-0 z-10 flex items-center gap-2 bg-slate-50 pb-3">
          <div className="flex-1">
            <SearchFilter
              onSearchChange={handleSearchChange}
              onStatusChange={handleStatusChange}
              selectedStatus={statusFilter}
            />
          </div>
          <button
            onClick={() => navigate("/projects/new")}
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm transition-all active:scale-95"
            aria-label="새 프로젝트 만들기"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>

        {/* 프로젝트 목록 */}
        {filteredProjects.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center text-center">
            <svg
              className="mb-3 h-12 w-12 text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-gray-500">{getEmptyMessage()}</p>
            {(searchQuery || statusFilter) && (
              <button
                onClick={handleResetFilters}
                className="mt-3 text-sm text-blue-600 hover:underline"
              >
                필터 초기화
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {/* 검색 결과 개수 표시 */}
            {(searchQuery || statusFilter) && (
              <p className="text-xs text-gray-500">{filteredProjects.length}개의 프로젝트</p>
            )}
            {filteredProjects.map((project: Project) => {
              const isCached = cachedProjectIds.has(project._id);
              const isUnavailable = isOffline && !isCached;

              return (
                <div
                  key={project._id}
                  onClick={() => handleProjectClick(project._id)}
                  className={`group relative cursor-pointer overflow-hidden rounded-xl border transition-all duration-200 ${
                    isUnavailable
                      ? "border-gray-200 bg-gray-100/50"
                      : isCached && isOffline
                        ? "border-l-4 border-l-emerald-400 border-t-gray-100 border-r-gray-100 border-b-gray-100 bg-white shadow-sm active:scale-[0.98]"
                        : "border-gray-100 bg-white shadow-sm active:scale-[0.98] active:bg-gray-50"
                  }`}
                >
                  {/* 미캐시 프로젝트 오버레이 */}
                  {isUnavailable && (
                    <>
                      {/* 대각선 스트라이프 패턴 */}
                      <div
                        className="pointer-events-none absolute inset-0 opacity-[0.04]"
                        style={{
                          backgroundImage: `repeating-linear-gradient(
                            -45deg,
                            #64748b 0px,
                            #64748b 1px,
                            transparent 1px,
                            transparent 6px
                          )`,
                        }}
                      />
                      {/* 잠금 오버레이 */}
                      <div className="pointer-events-none absolute right-3 top-3 z-10 flex items-center gap-1 rounded-full bg-gray-200/80 px-2 py-1">
                        <Lock className="h-3 w-3 text-gray-500" />
                        <span className="text-[10px] font-medium text-gray-500">온라인 필요</span>
                      </div>
                    </>
                  )}

                  <div className={`relative p-4 ${isUnavailable ? "opacity-50" : ""}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        {/* 캐시 상태 아이콘 (오프라인일 때만 표시) */}
                        {isOffline && isCached && (
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50">
                            <CheckCircle2
                              className="h-4 w-4 text-emerald-500"
                              aria-label="캐시됨"
                            />
                          </div>
                        )}
                        {isOffline && !isCached && (
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                            <CloudOff className="h-4 w-4 text-gray-400" aria-label="온라인 필요" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          {project.number && (
                            <p
                              className={`text-xs ${isUnavailable ? "text-gray-400" : "text-gray-500"}`}
                            >
                              {project.number}
                            </p>
                          )}
                          <h3
                            className={`font-semibold truncate ${isUnavailable ? "text-gray-400" : "text-slate-800"}`}
                          >
                            {project.name}
                          </h3>
                        </div>
                      </div>
                      <StatusBadge status={project.status || "PENDING"} dimmed={isUnavailable} />
                    </div>
                    <div
                      className={`mt-3 flex items-center gap-2 text-xs ${isUnavailable ? "text-gray-300" : "text-gray-500"}`}
                    >
                      <svg
                        className="h-3.5 w-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span>
                        {formatDate(project.startDate)} ~ {formatDate(project.endDate)}
                      </span>
                    </div>
                  </div>

                  {/* 캐시됨 호버 효과 (온라인 또는 캐시됨일 때만) */}
                  {!isUnavailable && (
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/0 to-white/0 opacity-0 transition-opacity group-active:opacity-100" />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, dimmed = false }: { status: ProjectStatus; dimmed?: boolean }) {
  const style = getProjectStatusStyle(status);
  const label = PROJECT_STATUS_LABELS[status] || status;

  return (
    <span
      className={`rounded-full px-2 py-1 text-xs font-medium ${
        dimmed ? "bg-gray-100 text-gray-400" : `${style.bgColor} ${style.color}`
      }`}
    >
      {label}
    </span>
  );
}
